import { useEffect, useState } from "react";
import { Share, X } from "lucide-react";

const AppleGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.03 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.702" />
  </svg>
);

const AndroidGlyph = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.0989L4.841 5.4467a.4161.4161 0 00-.5677-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3432-4.1021-2.6889-7.5743-6.1185-9.4396" />
  </svg>
);

// Minimal footer install action. One tap installs where the browser allows it
// (Android/desktop Chrome -> native prompt). iOS Safari can't be triggered
// programmatically (Apple restriction), so there we show the smallest possible
// two-tap hint: Share -> Add to Home Screen. No store wording or badges.
export default function InstallApp() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [hint, setHint] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    setInstalled(window.matchMedia("(display-mode: standalone)").matches || !!window.navigator.standalone);
    const ua = navigator.userAgent || "";
    setIsIOS(/iPhone|iPad|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setHint(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => {});
      setDeferred(null);
      return;
    }
    setHint(true);
  };

  return (
    <>
      <button type="button" onClick={click} data-testid="footer-install-app"
        className="group text-left hover:text-white transition-colors duration-200">
        <span className="flex items-center gap-2 text-zinc-500 group-hover:text-zinc-300 transition-colors duration-200">
          <AppleGlyph className="h-3.5 w-3.5" />
          <AndroidGlyph className="h-3.5 w-3.5" />
        </span>
        <span className="block mt-1.5 leading-snug">Get HiredToday on your phone</span>
        <span className="block text-[11px] text-zinc-500 mt-0.5 group-hover:text-zinc-400 transition-colors duration-200">
          Android &amp; iPhone
        </span>
      </button>

      {hint && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4 bg-zinc-950/50"
          onClick={() => setHint(false)} data-testid="install-hint-backdrop">
          <div className="relative bg-white rounded-lg shadow-2xl px-5 py-4 w-full max-w-xs text-center"
            onClick={(e) => e.stopPropagation()} data-testid="install-hint">
            <button type="button" onClick={() => setHint(false)} aria-label="Close" data-testid="install-hint-close"
              className="absolute top-2.5 right-2.5 p-1 text-zinc-400 hover:text-zinc-700 transition-colors">
              <X className="h-4 w-4" />
            </button>
            <span className="inline-flex h-10 w-10 rounded-md bg-zinc-950 text-white items-center justify-center">
              {isIOS ? <Share className="h-5 w-5" /> : <AndroidGlyph className="h-5 w-5" />}
            </span>
            <p className="mt-3 text-sm font-semibold text-zinc-950">Almost there</p>
            {isIOS ? (
              <p className="mt-1 text-sm text-zinc-600 leading-relaxed">
                Tap <strong>Share</strong> <Share className="inline h-3.5 w-3.5 -mt-0.5 text-blue-700" /> below,
                then <strong>Add to Home Screen</strong> and tap <strong>Add</strong>.
              </p>
            ) : (
              <p className="mt-1 text-sm text-zinc-600 leading-relaxed">
                Open the browser menu <strong>⋮</strong> and tap <strong>Add to Home screen</strong>.
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
